import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit3, Trash2 } from 'lucide-react';
import { Loader } from '../../components/Common/Loader';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchIncentiveDetails, deleteIncentive } from '../../store/incentiveSlice';

export const IncentiveDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selectedIncentiveDetails: incentive, loading } = useAppSelector((state) => state.incentive);

  useEffect(() => {
    if (id) {
      dispatch(fetchIncentiveDetails(id));
    }
  }, [id, dispatch]);

  const handleRemove = async () => {
    if (!incentive) return;
    if (window.confirm(`Are you sure you want to remove incentive "${incentive.name}"?`)) {
      try {
        await dispatch(deleteIncentive(incentive.id)).unwrap();
        navigate('/incentive');
      } catch (err: any) {
        alert(err || 'Failed to remove incentive.');
      }
    }
  };

  const handleEdit = () => {
    if (incentive) {
      navigate(`/incentive/edit/${incentive.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Loader text="Loading incentive details..." spinnerSize="lg" />
      </div>
    );
  }

  if (!incentive) {
    return (
      <div className="min-h-screen p-6 select-none">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/incentive')}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all text-white flex items-center justify-center cursor-pointer shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">Incentive Program Not Found</h1>
        </div>
        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm text-center text-gray-500">
          No details available for incentive program "{id}".
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen select-none pb-12">
      {/* Top Header Bar matching Screenshot 3 */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        {/* Title with Back Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/incentive')}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all text-white flex items-center justify-center cursor-pointer shadow-sm"
            title="Go back to incentives list"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight m-0">
            {incentive.name}
          </h1>
        </div>

        {/* Action Buttons matching Screenshot 3 */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={handleEdit}
            className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-bold py-2 px-4 rounded-xl flex items-center gap-2 cursor-pointer transition-colors shadow-xs"
          >
            <Edit3 className="w-4 h-4" />
            <span>Edit Incentive</span>
          </button>

          <button
            onClick={handleRemove}
            className="bg-white border border-rose-200 hover:bg-rose-50 text-rose-600 text-xs font-bold py-2 px-4 rounded-xl flex items-center gap-2 cursor-pointer transition-colors shadow-xs"
          >
            <Trash2 className="w-4 h-4" />
            <span>Remove Incentive</span>
          </button>
        </div>
      </div>

      {/* Main Details Card matching Screenshot 3 */}
      <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm space-y-6">
        {/* Card Header & Status Badge */}
        <div className="flex items-center justify-between border-b border-gray-100 pb-4">
          <h2 className="text-base md:text-lg font-bold text-gray-900 m-0">
            Incentive Details
          </h2>
          <span className={`text-[11px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wider ${
            incentive.status === 'Active' ? 'bg-emerald-100 text-emerald-600' :
            incentive.status === 'Inactive' || incentive.status === 'Expired' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
          }`}>
            {incentive.status}
          </span>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-y-6 gap-x-8 text-xs md:text-sm">
          {/* Incentive ID */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Incentive ID</div>
            <div className="font-bold text-gray-900 font-mono tracking-tight">{incentive.id || '-'}</div>
          </div>

          {/* Product */}
          <div className="md:col-span-1 lg:col-span-2">
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Product</div>
            <div className="flex items-center gap-2 font-bold text-gray-900">
              <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <span>{incentive.product || '-'}</span>
            </div>
          </div>

          {/* Product SAP Code */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Product SAP Code</div>
            <div className="font-bold text-gray-800 font-mono">{incentive.sapCode || '-'}</div>
          </div>

          {/* Zone */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Zone</div>
            <div className="font-bold text-gray-800">{incentive.zone || '-'}</div>
          </div>

          {/* Distributor Type */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Distributor Type</div>
            <div className="font-bold text-gray-800">{incentive.distType || '-'}</div>
          </div>

          {/* Created by */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Created by</div>
            <div className="font-bold text-gray-800">{incentive.createdBy || '-'}</div>
          </div>

          {/* Extended by */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Extended by</div>
            <div className="font-bold text-gray-800">{incentive.extendedBy || '-'}</div>
          </div>

          {/* Created On */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Created On</div>
            <div className="font-bold text-gray-800">{incentive.createdOn || '-'}</div>
          </div>

          {/* Extended On */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Extended On</div>
            <div className="font-bold text-gray-800">{incentive.extendedOn || '-'}</div>
          </div>

          {/* Duration */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Duration</div>
            <div className="font-bold text-gray-800">{incentive.duration || incentive.validity || '-'}</div>
          </div>
        </div>

        {/* Cities Section */}
        <div className="border-t border-gray-100 pt-6">
          <div className="text-gray-400 font-medium mb-2 text-[11px]">Cities</div>
          <div className="flex flex-wrap gap-2 font-semibold text-xs text-gray-800">
            {incentive.citiesList && incentive.citiesList.length > 0 ? (
              incentive.citiesList.map((c, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-50 text-gray-700 border border-gray-200/60"
                >
                  {c}
                </span>
              ))
            ) : (
              <span>{incentive.city || '-'}</span>
            )}
          </div>
        </div>

        {/* Incentive Mechanics Section */}
        <div className="border-t border-gray-100 pt-6">
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-5 h-5 text-[#B71C1C]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
            <h3 className="font-bold text-[#B71C1C] text-sm md:text-base m-0">
              Incentive Mechanics
            </h3>
          </div>

          <div className="flex flex-wrap items-center gap-8 md:gap-16 pt-2">
            {/* Target Qty */}
            <div>
              <div className="text-xs text-gray-400 font-medium mb-1">Target Quantity</div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl md:text-3xl font-extrabold text-gray-900">
                  {incentive.targetQuantity ?? 0}
                </span>
              </div>
            </div>

            {/* Red Arrow */}
            <div className="text-[#B71C1C] text-xl font-bold">
              →
            </div>

            {/* Earn */}
            <div>
              <div className="text-xs text-gray-400 font-medium mb-1">Earn Reward</div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl md:text-3xl font-extrabold text-[#B71C1C]">
                  {incentive.reward || '₹0'}
                </span>
                <span className="text-xs md:text-sm font-semibold text-[#B71C1C]">
                  ({incentive.payout || 'CREDIT'}) for {incentive.product || 'Product'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
